import { ChangeDetectorRef, Injectable } from '@angular/core';
import { IItemsListAction2 } from '@fi-shared/fi-items-list/fi-items-list';
import { FiItemsListOptionType } from '@fi-shared/fi-items-list/fi-items-list-option-type';
import { AppTextService } from '@fi-shared/app-text';
import { FiChecklist2Service } from '@fi-shared/checklist/fi-checklist2.service';
import {
    IChecklistV2,
    IChecklistV2ForList,
    IChecklistV2ViewerBase,
    IChecklistV2ViewerTemplateBase,
} from '@fi-shared/checklist/checklist-v2';
import { AccessRightType, ApplicationSettingsType, ObjectTypes, SideListOptionType } from '@fi-shared/types';
import { FiStateNames } from '@fi-shared/fi-state-proxy/fi-state-names';
import { FiCustomerDataService } from '@fi-shared/fi-shared-services/fi-customer-data.service';
import { StateAccessCheckService } from '@fi-shared/fi-shared-services/state-access-check.service';
import { FiChecklist2TemplateService } from '@fi-shared/checklist/fi-checklist2-template/fi-checklist2-template.service';
import { FiStateService } from '@fi-shared/fi-state-proxy/fi-state.proxy.service';
import { AccessToApplicationTypeService } from '@fi-shared/fi-shared-services/access-to-application-type.service';
import { FiChecklistHelperService } from '@fi-shared/checklist/fi-checklist-helper.service';
import { FiDialogsService } from '@fi-shared/fi-dialogs';
import { getObjectTypeNameTextId } from '@fi-shared/utils';
import { FiChecklistScheduleType } from '@fi-shared/checklist/schedule/schedule-edit/fi-checklist-schedule-type';
import { FiUserDataService } from '@fi-shared/fi-shared-services/fi-user-data.service';
import { FiChecklist2ReportStateService } from '@fi-shared/checklist/fi-checklist2-report/services/fi-checklist2-report-state.service';
import { FiChecklistStoreService } from '@fi-shared/checklist/fi-checklist-store.service';
import { FiSnackBarService } from '@fi-shared/fi-snackbar/snack-bar.service';
import { FiChecklist2ScheduleDialogService } from '@fi-shared/checklist/schedule/services/fi-checklist2-schedule-dialog.service';
import { ChecklistViewerStatus } from '@fi-shared/checklist/fi-checklist-viewer-status';
import { FiItemsDeleteHelperService } from '@fi-shared/fi-shared-services/fi-items-delete-helper.service';

@Injectable()
export class FiChecklistActionsHelperService {
    constructor(
        private appTextService: AppTextService,
        private fiChecklist2Service: FiChecklist2Service,
        private fiChecklistHelperService: FiChecklistHelperService,
        private customerDataService: FiCustomerDataService,
        private userDataService: FiUserDataService,
        private stateAccessCheckService: StateAccessCheckService,
        private fiChecklist2TemplateService: FiChecklist2TemplateService,
        private fiChecklistStoreService: FiChecklistStoreService,
        private fiStateService: FiStateService,
        private fiDialogsService: FiDialogsService,
        private snackBar: FiSnackBarService,
        private fiChecklist2ScheduleDialogService: FiChecklist2ScheduleDialogService,
        private fiChecklist2ReportStateService: FiChecklist2ReportStateService,
        private fiItemsDeleteHelperService: FiItemsDeleteHelperService,
        private accessToApplicationTypeService: AccessToApplicationTypeService,
    ) {}

    //#region checklist actions
    getChecklistListActions(args: {
        auditMode: boolean;
        templateMode: boolean;
        cdr: ChangeDetectorRef;
        removeDataFn: (item: IChecklistV2ForList) => void;
    }): IItemsListAction2<IChecklistV2ForList>[] {
        const actions: IItemsListAction2<IChecklistV2ForList>[] = [
            this.getChecklistEditAction<IChecklistV2ForList>({
                checklistIdFn: (data) => data.Id,
                auditMode: args.auditMode,
                templateMode: args.templateMode,
            }),
            this.getViewReportAction<IChecklistV2ForList>({
                onClick: (data) => {
                    this.fiChecklist2ReportStateService.goToReport(data.Id, data.ScheduleId, data, args.auditMode);
                },
                businessUnitId: this.userDataService.getUserBusinessUnitId(),
                auditMode: args.auditMode,
            }),
            this.getChecklistGenerateTemplateAction({
                checklistIdFn: (data) => data.Id,
                auditMode: args.auditMode,
            }),
            this.getChecklistGenerateAuditChecklistAction({
                checklistIdFn: (data) => data.Id,
                auditMode: args.auditMode,
            }),
            this.getChecklistGlobalMobileNotificationsAction<IChecklistV2ForList>({
                getChecklistNameFn: (item) => item.Name,
                onSuccessFn: (data) => {},
                auditMode: args.auditMode,
            }),
            this.getChecklistPersonalMobileNotificationsAction<IChecklistV2ForList>({
                getChecklistNameFn: (item) => item.Name,
                onSuccessFn: (data) => {},
                auditMode: args.auditMode,
            }),
            this.getDeleteChecklistResponsesAction<IChecklistV2ForList>({
                getChecklistToScheduleIdFn: (data) => data.ChecklistToScheduleId,
                getChecklistNameFn: (data) => data.Name,
                getScheduleNameFn: (data) => data.ScheduleName,
                disabled: (data) => !data.HasAnswers,
                afterDeleteFn: (data) => {
                    data.HasAnswers = false;
                    data.FilledCheckpointsCount = 0;

                    const textMessage = this.appTextService.get('checklist.responses_deleted');
                    this.snackBar.showSnackbar({ textMessage });

                    this.fiChecklistStoreService.triggerChecklistListDataChanged(data);
                    args.cdr.markForCheck();
                },
                businessUnitId: this.userDataService.getUserBusinessUnitId(),
                auditMode: args.auditMode,
            }),
            this.getChecklistUnitNotApplicableAction({
                getUnitNameFn: (data) => data.BusinessUnitName,
                getChecklistNameFn: (data) => data.Name,
                hasAnswersFn: (data) => data.FilledCheckpointsCount > 0,
                onSuccess: (data) => {
                    data.FilledCheckpointsCount = 0;
                    this.fiChecklistStoreService.triggerChecklistListDataChanged(data);
                },
                businessUnitId: this.userDataService.getUserBusinessUnitId(),
                auditMode: args.auditMode,
            }),
            this.getScheduleChecklistAction({
                checklistIdFn: (item) => item.Id,
                auditMode: args.auditMode,
                templateMode: args.templateMode,
            }),
            this.getDeleteChecklistAction<IChecklistV2ForList>({
                onClick: (data) => {
                    this.fiChecklistHelperService
                        .getConfirmDeleteChecklistDialog({
                            item: data,
                            checklistIdFn: () => data.Id,
                            auditMode: args.auditMode,
                        })
                        .then((result) => {
                            if (result.result) {
                                this.fiChecklist2Service.deleteChecklists([data.Id], args.auditMode).then(() => {
                                    const objectType = !args.auditMode
                                        ? ObjectTypes.Checklist2
                                        : ObjectTypes.Checklist2Audit;
                                    this.fiItemsDeleteHelperService.showDeletedObjectSimpleSnackBar(
                                        objectType,
                                        [data.Id],
                                        data.Name,
                                    );

                                    args.removeDataFn(data); // for excluding deleted checklist from list
                                });
                            }
                        });
                },
                auditMode: args.auditMode,
                templateMode: args.templateMode,
            }),
        ];
        return actions;
    }

    getChecklistEditAction<T extends IChecklistV2ViewerBase>(args: {
        checklistIdFn: (item: T) => number;
        auditMode: boolean;
        templateMode: boolean;
    }): IItemsListAction2<T> {
        const textId = getObjectTypeNameTextId(!args.auditMode ? ObjectTypes.Checklist2 : ObjectTypes.Checklist2Audit);
        return {
            title: this.appTextService.get('checklist.edit_checklist_v2', {
                '{0}': this.appTextService.get(textId).toLowerCase(),
            }),
            condition: (data) => {
                return (
                    !args.templateMode &&
                    this.fiChecklist2Service.isChecklistShareRights(args.auditMode) &&
                    data.HasEditAccessToChecklist &&
                    data.HasAccessToChecklist
                );
            },
            onClick: (data) => {
                const rootState = FiStateNames.getChecklistV2RootState(args.auditMode);

                // for returning to current list
                this.fiStateService.registerReturnToCurrentState(rootState.Edit._name);

                this.fiStateService.go(rootState.Edit._name, {
                    id: args.checklistIdFn(data),
                });
            },
            iconClass: 'fa-regular fa-pen-to-square',
            optionType: FiItemsListOptionType.Icon,
        };
    }

    getCompletionModeAction(auditMode: boolean): IItemsListAction2<IChecklistV2ViewerBase> {
        return {
            title: this.appTextService.get('checklist.completion_mode'),
            condition: (data) => {
                return (
                    this.fiChecklistHelperService.getViewerStatus(data) === ChecklistViewerStatus.Active &&
                    data.BusinessUnitId === this.userDataService.getUserBusinessUnitId()
                );
            },
            onClick: (data) => {
                this.fiStateService.go(
                    FiStateNames.getChecklistV2RootState(auditMode).View,
                    {
                        id: data.ChecklistToScheduleId,
                        businessUnitId: data.BusinessUnitId,
                        isPreviewMode: false,
                        previewPeriodId: undefined,
                    },
                    { reload: true },
                );
            },
            iconClass: 'far fa-ballot-check',
            optionType: FiItemsListOptionType.Icon,
        };
    }

    getViewReportAction<T extends IChecklistV2ViewerBase>(args: {
        onClick: (item: T) => void;
        businessUnitId: number;
        auditMode: boolean;
    }): IItemsListAction2<T> {
        return {
            title: this.appTextService.get('checklist.view_report'),
            condition: (data) => {
                return (
                    this.fiChecklist2Service.isReportReadRights(args.auditMode) &&
                    data.IsReportAvailable &&
                    !this.fiChecklistHelperService.isAuditNotStarted(args.businessUnitId)
                );
            },
            disabled: (data) => {
                return !data.ClosestPrevPeriodId;
            },
            onClick: (data) => args.onClick(data),
            iconClass: 'fa-regular fa-chart-pie',
            optionType: FiItemsListOptionType.Icon,
        };
    }

    private getMobileNotificationsChangedText(textId: string, isOn: boolean, checklistName: string): string {
        return this.appTextService.get(textId, {
            '{state}': this.appTextService.get(isOn ? 'on' : 'off').toUpperCase(),
            '{checklist_name}': checklistName,
        });
    }

    private getChecklistNotApplicableChangedText(
        isNotApplicable: boolean,
        checklistName: string,
        unitName: string,
    ): string {
        const text = this.appTextService.get('checklist.not_applicable_changed', {
            '{state}': this.appTextService.get(
                isNotApplicable ? 'checklist.not_applicable_v2' : 'checklist.applicable',
            ),
            '{checklist_name}': checklistName,
            '{unit_name}': unitName,
        });
        return text;
    }

    getChecklistGlobalMobileNotificationsAction<T extends IChecklistV2ViewerBase>(args: {
        onSuccessFn: (item: T) => void;
        getChecklistNameFn: (item: T) => string;
        auditMode: boolean;
    }): IItemsListAction2<T> {
        return {
            title: this.appTextService.get('checklist.global_mobile_notifications'),
            condition: (data) => {
                return this.fiChecklist2Service.canEditScheduleBySharingSelection(data, args.auditMode) && !args.auditMode;
            },
            onClick: (data) => {
                this.fiChecklist2Service
                    .setChecklistIsGlobalMobileNotifications(
                        data.ChecklistToScheduleId,
                        !data.IsGlobalMobileNotifications,
                    )
                    .then(() => {
                        data.IsGlobalMobileNotifications = !data.IsGlobalMobileNotifications;
                        args.onSuccessFn(data);
                        this.snackBar.showSnackbar({
                            // show snackbar ?
                            textMessage: this.getMobileNotificationsChangedText(
                                'checklist.global_notifications_changed',
                                data.IsGlobalMobileNotifications,
                                args.getChecklistNameFn(data),
                            ),
                        });
                    });
            },
            isChecked: (data) => data.IsGlobalMobileNotifications,
            optionType: FiItemsListOptionType.Checkbox,
            getTooltipText: (data) => this.appTextService.get('checklist.global_mobile_notifications_tooltip_text'),
        };
    }

    getChecklistPersonalMobileNotificationsAction<T extends IChecklistV2ViewerBase>(args: {
        onSuccessFn: (item: T) => void;
        getChecklistNameFn: (item: T) => string;
        auditMode: boolean;
    }): IItemsListAction2<T> {
        return {
            title: this.appTextService.get('checklist.turn_off_reminder'),
            condition: (data) => {
                return data.IsGlobalMobileNotifications && data.HasAccessToSchedule && !args.auditMode;
            },
            onClick: (data) => {
                this.fiChecklist2Service
                    .setChecklistIsMobileNotifications(data.ChecklistToScheduleId, !data.IsMobileNotifications)
                    .then(() => {
                        data.IsMobileNotifications = !data.IsMobileNotifications;
                        args.onSuccessFn(data);
                        this.snackBar.showSnackbar({
                            // show snackbar ?
                            textMessage: this.getMobileNotificationsChangedText(
                                'checklist.personal_notifications_changed',
                                data.IsMobileNotifications,
                                args.getChecklistNameFn(data),
                            ),
                        });
                    });
            },
            isChecked: (data) => data.IsMobileNotifications,
            optionType: FiItemsListOptionType.Checkbox,
            getTooltipText: (data) => this.appTextService.get('checklist.personal_mobile_notifications_tooltip_text'),
        };
    }

    getChecklistUnitNotApplicableAction<T extends IChecklistV2ViewerBase>(args: {
        hasAnswersFn: (item: T) => boolean;
        getChecklistNameFn: (item: T) => string;
        getUnitNameFn: (item: T) => string;
        onSuccess: (item: T) => void;
        businessUnitId: number;
        auditMode: boolean;
    }): IItemsListAction2<T> {
        return {
            title: this.appTextService.get('checklist.not_applicable'),
            condition: (data) => {
                // https://chainformation.atlassian.net/browse/FI-5683
                // don't allow users make audit checklists Not applicable
                // https://chainformation.atlassian.net/browse/FI-5619
                // don't allow users make on demand checklists Not applicable
                return (
                    !args.auditMode &&
                    data.ScheduleType !== FiChecklistScheduleType.OnDemand &&
                    this.fiChecklist2Service.isChecklistShareRights(args.auditMode) &&
                    data.HasAccessToSchedule &&
                    data.ScheduleHasAccessToBusinessUnit &&
                    !this.fiChecklistHelperService.isAuditNotStarted(args.businessUnitId)
                );
            },
            onClick: (data) => {
                const hasAnswers = args.hasAnswersFn(data);
                const setNotApplicableFn = () => {
                    const newIsNotApplicable = !data.IsSetNotApplicable;
                    this.fiChecklist2Service
                        .setChecklistIsNotApplicable(
                            data.ChecklistToScheduleId,
                            newIsNotApplicable,
                            args.auditMode,
                            data.BusinessUnitId,
                        )
                        .then(() => {
                            data.IsSetNotApplicable = newIsNotApplicable;
                            const snackBarTextMessage = this.getChecklistNotApplicableChangedText(
                                newIsNotApplicable,
                                args.getChecklistNameFn(data),
                                args.getUnitNameFn(data),
                            );
                            this.snackBar.showSnackbar({
                                textMessage: snackBarTextMessage,
                            });
                            args.onSuccess(data);
                        });
                };

                const shouldShowWarning = !data.IsSetNotApplicable && data.IsActive && hasAnswers;
                if (!shouldShowWarning) {
                    setNotApplicableFn();
                } else {
                    this.fiDialogsService
                        .confirm({
                            isTopCloseButtonShown: true,
                            colorClass: 'orange',
                            headerIconClass: 'orange far fa-circle-exclamation',
                            headerText: this.appTextService.get(
                                'checklist.checklist_save_not_applicable_warning_header',
                            ),
                            messageText: this.appTextService.get(
                                'checklist.checklist_save_not_applicable_warning_message',
                            ),
                            isNoButtonPrimaryColor: true,
                            yesButtonText: this.appTextService.get('common.confirm'),
                            preventUppercaseButtonLabel: true,
                            config: {
                                panelClass: 'fi-rounded-dialog-panel',
                                maxWidth: '640px',
                                autoFocus: false,
                            },
                        })
                        .onYesButtonClick(() => {
                            setNotApplicableFn();
                        });
                }
            },
            isChecked: (data) => data.IsSetNotApplicable,
            optionType: FiItemsListOptionType.Checkbox,
        };
    }

    getDeleteChecklistResponsesAction<T extends IChecklistV2ViewerBase>(args: {
        getChecklistToScheduleIdFn: (item: T) => number;
        getChecklistNameFn: (item: T) => string;
        getScheduleNameFn: (item: T) => string;
        afterDeleteFn: (item: T) => void;
        disabled: (item: T) => boolean;
        businessUnitId: number;
        auditMode: boolean;
    }): IItemsListAction2<T> {
        return {
            title: this.appTextService.get('checklist.delete_responses'),
            condition: (data) => {
                return this.fiChecklist2Service.canEditScheduleBySharingSelection(data, args.auditMode);
            },
            onClick: (data) => {
                const checklistToScheduleId = args.getChecklistToScheduleIdFn(data);
                this.fiChecklist2Service
                    .getChecklistAnswersCount(checklistToScheduleId, args.auditMode)
                    .then((answersCount) => {
                        this.fiChecklistHelperService
                            .getConfirmDeleteChecklistResponsesDialog(
                                args.getChecklistNameFn(data),
                                args.getScheduleNameFn(data),
                                answersCount,
                            )
                            .onYesButtonClick(() => {
                                this.fiChecklist2Service
                                    .deleteChecklistAnswers(checklistToScheduleId, args.auditMode)
                                    .then(() => {
                                        args.afterDeleteFn(data);
                                    });
                            });
                    });
            },
            disabled: (data) => args.disabled(data),
            iconClass: 'fa-regular fa-trash',
            optionType: FiItemsListOptionType.Icon,
        };
    }

    getChecklistGenerateTemplateAction<T extends IChecklistV2ViewerBase>(args: {
        checklistIdFn: (item: T) => number;
        auditMode: boolean;
    }): IItemsListAction2<T> {
        const textId = getObjectTypeNameTextId(
            !args.auditMode ? ObjectTypes.Checklist2Template : ObjectTypes.Checklist2AuditTemplate,
        );
        return {
            title: this.appTextService.get('checklist.generate_item', {
                '{0}': this.appTextService.get(textId).toLowerCase(),
            }),
            condition: (data) => {
                const applicationType = !args.auditMode
                    ? ApplicationSettingsType.ChecklistV2Local
                    : ApplicationSettingsType.ChecklistV2AuditLocal;
                return (
                    data.HasAccessToChecklist &&
                    this.accessToApplicationTypeService.HasAccessToApplicationType(
                        applicationType,
                        AccessRightType.Admin,
                    )
                );
            },
            onClick: (data) => {
                const checklistId = args.checklistIdFn(data);
                this.fiChecklist2TemplateService
                    .getTemplateFromChecklist(checklistId, args.auditMode)
                    .then((checklistTemplate: IChecklistV2) => {
                        this.fiStateService.go(
                            FiStateNames.getChecklistV2RootState(args.auditMode).Template.Add._name,
                            {
                                checklistTemplate,
                                isTemplateBased: false,
                            },
                        );
                    });
            },
            iconClass: 'fa-regular fa-wand-magic-sparkles',
            optionType: FiItemsListOptionType.Icon,
        };
    }

    getScheduleChecklistAction<T extends IChecklistV2ViewerBase>(args: {
        checklistIdFn: (item: T) => number;
        auditMode: boolean;
        templateMode: boolean;
    }): IItemsListAction2<T> {
        const textId = getObjectTypeNameTextId(!args.auditMode ? ObjectTypes.Checklist2 : ObjectTypes.Checklist2Audit);
        return {
            title: this.appTextService.get('checklist.schedule_checklist', {
                '{0}': this.appTextService.get(textId).toLowerCase(),
            }),
            condition: (data) => {
                return (
                    !args.templateMode &&
                    this.fiChecklist2Service.isChecklistShareRights(args.auditMode) &&
                    !data.DisableChecklistSchedule && // if checklist is not scheduled and not shared only creator can schedule it
                    this.fiChecklistHelperService.isChecklistNotScheduled(data)
                );
            },
            onClick: (data) => {
                this.fiChecklist2ScheduleDialogService.openAddChecklistScheduleDialogWithChecklistId(
                    args.checklistIdFn(data),
                    args.auditMode,
                );
            },
            iconClass: 'fa-regular fa-calendar-week',
            optionType: FiItemsListOptionType.Icon,
        };
    }

    getDeleteChecklistAction<T extends IChecklistV2ViewerBase>(args: {
        onClick: (item: T) => void;
        auditMode: boolean;
        templateMode: boolean;
    }): IItemsListAction2<T> {
        const textId = getObjectTypeNameTextId(!args.auditMode ? ObjectTypes.Checklist2 : ObjectTypes.Checklist2Audit);
        return {
            title: this.appTextService.get('common.delete_item', {
                '{0}': this.appTextService.get(textId).toLowerCase(),
            }),
            condition: (data) => {
                return (
                    !args.templateMode &&
                    this.fiChecklist2Service.isChecklistShareRights(args.auditMode) &&
                    data.HasEditAccessToChecklist &&
                    data.HasAccessToChecklist
                );
            },
            onClick: (data) => args.onClick(data),
            iconClass: 'fa-regular fa-trash',
            optionType: FiItemsListOptionType.Icon,
        };
    }

    getChecklistGenerateAuditChecklistAction<T extends IChecklistV2ViewerBase>(args: {
        checklistIdFn: (item: T) => number;
        auditMode: boolean;
    }): IItemsListAction2<T> {
        return {
            title: this.appTextService.get(
                !args.auditMode ? 'checklist.generate_audit_checklist' : 'checklist.generate_checklist',
            ),
            condition: (data) => {
                const targetApplicationSettingType = !args.auditMode
                    ? ApplicationSettingsType.ChecklistV2AuditLocal
                    : ApplicationSettingsType.ChecklistV2Local;
                return (
                    data.HasAccessToChecklist &&
                    this.accessToApplicationTypeService.HasAccessToApplicationType(
                        targetApplicationSettingType,
                        AccessRightType.Admin,
                    )
                );
            },
            onClick: (data) => {
                const checklistId = args.checklistIdFn(data);
                this.fiChecklist2Service
                    .getChecklistCopy(checklistId, args.auditMode)
                    .then((checklist: IChecklistV2) => {
                        const targetAuditMode = !args.auditMode;

                        const targetRootState = FiStateNames.getChecklistV2RootState(targetAuditMode);
                        // for returning to proper list with created by filter
                        this.fiStateService.registerReturnToState({
                            fromState: targetRootState.Add._name,
                            toState: targetRootState.List,
                            toStateParams: { filterType: SideListOptionType.CreatedBy },
                        });

                        this.fiStateService.go(targetRootState.Add._name, { checklist, isTemplateBased: false });
                    });
            },
            iconClass: 'fa-regular fa-wand-magic-sparkles',
            optionType: FiItemsListOptionType.Icon,
        };
    }

    //#endregion

    //#region checklist template actions
    getChecklistTemplateEditAction<T extends IChecklistV2ViewerTemplateBase>(args: {
        checklistTemplateIdFn: (item: T) => number;
        auditMode: boolean;
        templateMode: boolean;
    }): IItemsListAction2<T> {
        const rootState = FiStateNames.getChecklistV2RootState(args.auditMode);
        const textId = getObjectTypeNameTextId(
            !args.auditMode ? ObjectTypes.Checklist2Template : ObjectTypes.Checklist2AuditTemplate,
        );
        return {
            title: this.appTextService.get('checklist.edit_checklist_v2', {
                '{0}': this.appTextService.get(textId).toLowerCase(),
            }),
            condition: (data) => {
                return (
                    args.templateMode &&
                    this.fiChecklist2Service.isChecklistShareRights(args.auditMode) &&
                    data.HasEditAccessToChecklistTemplate
                );
            },
            onClick: (data) => {
                this.fiStateService.go(rootState.Template.Edit._name, { id: args.checklistTemplateIdFn(data) });
            },
            iconClass: 'fa-regular fa-pen-to-square',
            optionType: FiItemsListOptionType.Icon,
        };
    }

    getCreateChecklistFromTemplateAction<T extends IChecklistV2ViewerTemplateBase>(args: {
        checklistTemplateIdFn: (item: T) => number;
        auditMode: boolean;
        templateMode: boolean;
    }): IItemsListAction2<T> {
        const rootState = FiStateNames.getChecklistV2RootState(args.auditMode);
        const textId = getObjectTypeNameTextId(!args.auditMode ? ObjectTypes.Checklist2 : ObjectTypes.Checklist2Audit);
        return {
            title: this.appTextService.get('checklist.create_checklist_v2_from_template', {
                '{0}': this.appTextService.get(textId).toLowerCase(),
            }),
            condition: (data) => {
                return args.templateMode && this.fiChecklist2Service.isChecklistShareRights(args.auditMode);
            },
            /*disabled: data => {
                return !data.IsChecklistShared;
            },*/
            onClick: (data) => {
                this.fiStateService.go(rootState.Template.Copy._name, { id: args.checklistTemplateIdFn(data) });
            },
            iconClass: 'fa-regular fa-wand-magic-sparkles',
            optionType: FiItemsListOptionType.Icon,
        };
    }

    getDeleteChecklistTemplateAction<T extends IChecklistV2ViewerTemplateBase>(args: {
        onClick: (item: T) => void;
        auditMode: boolean;
        templateMode: boolean;
    }): IItemsListAction2<T> {
        const textId = getObjectTypeNameTextId(
            !args.auditMode ? ObjectTypes.Checklist2Template : ObjectTypes.Checklist2AuditTemplate,
        );
        return {
            title: this.appTextService.get('common.delete_item', {
                '{0}': this.appTextService.get(textId).toLowerCase(),
            }),
            condition: (data) => {
                return (
                    args.templateMode &&
                    this.fiChecklist2Service.isChecklistShareRights(args.auditMode) &&
                    data.HasEditAccessToChecklistTemplate
                );
            },
            onClick: (data) => args.onClick(data),
            iconClass: 'fa-regular fa-trash',
            optionType: FiItemsListOptionType.Icon,
        };
    }

    //#endregion
}
